import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  appendOrganizationClientIds,
  organizationClientIdsKey,
} from "../../lib/utils/organizationReportClientIds"

function inboxHealthKey({ organizationId, startDate, endDate, clientIds } = {}) {
  return `${String(organizationId ?? "")}|${String(startDate ?? "")}|${String(endDate ?? "")}|${organizationClientIdsKey(clientIds)}`
}

/**
 * GET /api/v1/report/organizations/{organizationId}/messages/inbox-health?startDate=&endDate=&client_ids=
 */
export const fetchOrganizationInboxHealth = createAsyncThunk(
  "organizationInboxHealth/fetch",
  async ({ organizationId, startDate, endDate, clientIds } = {}, { rejectWithValue }) => {
    try {
      if (organizationId == null || organizationId === "") {
        return rejectWithValue("organizationId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const query = new URLSearchParams({
        startDate,
        endDate,
      })
      appendOrganizationClientIds(query, clientIds)
      const data = await get(
        `report/organizations/${organizationId}/messages/inbox-health?${query}`,
      )

      return {
        data,
        organizationId,
        startDate,
        endDate,
        fetchKey: inboxHealthKey({ organizationId, startDate, endDate, clientIds }),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch organization inbox health")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const key = inboxHealthKey(arg)
      const state = getState().organizationInboxHealth
      if (state?.status === "loading" && state?.requestedFetchKey === key) return false
      if (state?.status === "succeeded" && state?.fetchedFetchKey === key) return false
      return true
    },
  },
)

const initialState = {
  data: null,
  status: "idle",
  error: null,
  organizationId: null,
  startDate: null,
  endDate: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
}

const organizationInboxHealthSlice = createSlice({
  name: "organizationInboxHealth",
  initialState,
  reducers: {
    clearOrganizationInboxHealth: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationInboxHealth.pending, (state, action) => {
        const arg = action.meta.arg || {}
        state.status = "loading"
        state.error = null
        state.requestedFetchKey = inboxHealthKey(arg)
      })
      .addCase(fetchOrganizationInboxHealth.fulfilled, (state, action) => {
        if (action.payload?.fetchKey !== state.requestedFetchKey) return
        state.status = "succeeded"
        state.data = action.payload?.data ?? null
        state.organizationId = action.payload?.organizationId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchOrganizationInboxHealth.rejected, (state, action) => {
        if (inboxHealthKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch organization inbox health"
        state.data = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearOrganizationInboxHealth } = organizationInboxHealthSlice.actions
export default organizationInboxHealthSlice.reducer
